import { useCallback, useMemo, useState } from 'react';
import {
  RULES, TARGETED_RULES, base, isHunt, ruleKind, sanitizeConfig,
  type Curriculum, type Grade, type LevelConfig, type RuleId,
} from '@numslearn/shared';
import { useSettings } from '../settings.js';
import { NumberField, Section, Segmented, Select, Toggle } from './controls.js';
import { MultiplierField, RangeFields } from './levelFields.js';
import { ruleLine } from '../game/verdict.js';
import {
  duplicateCurriculum, emptyCurriculum, mapLevel, mapStage, move,
  newLevel, newStage, useCurricula,
} from './useCurricula.js';
import type { StringKey, Translate } from '../i18n/index.js';

/**
 * The curriculum editor.
 *
 * The built-in Grades 1–6 are the app's opinion about a syllabus, not a
 * teacher's. This is where a teacher replaces it: stages they name, exercises
 * they configure, in the order they teach them. A stage still declares which
 * school year it counts as, because badges are earned against a year — that
 * is the one thing renaming cannot take with it.
 */

const RULE_IDS = Object.keys(RULES) as RuleId[];
const PAIR_RULES = RULE_IDS.filter((r) => ruleKind(r) === 'pair');
const HUNT_RULES = RULE_IDS.filter((r) => ruleKind(r) === 'hunt');
const GRADE_VALUES: Grade[] = [1, 2, 3, 4, 5, 6];

/**
 * A stage or level name is either the teacher's own words or a translation key.
 *
 * The built-in curriculum ships keys so it arrives in the reader's language;
 * the moment a teacher types over one it becomes plain text and stays theirs.
 */
function displayName(name: string, t: Translate): string {
  if (name.startsWith('key:')) return t(name.slice(4) as StringKey);
  const [prefix, arg] = name.split(':');
  // Through `t` with the number as a var, so it picks up Arabic-Indic numerals
  // rather than arriving as a bare Latin "1".
  if (prefix === 'msg.grade' && arg) return t('msg.grade', { g: Number(arg) });
  return name;
}

export function CurriculumEditor({ onClose }: { onClose: () => void }) {
  const { t, num } = useSettings();
  const store = useCurricula();
  const [draft, setDraft] = useState<Curriculum | null>(null);
  const [openLevel, setOpenLevel] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState(0);

  const edit = useCallback((fn: (c: Curriculum) => Curriculum) => {
    setDraft((c) => (c ? fn(c) : c));
    setSavedAt(0);
  }, []);

  const save = async () => {
    if (!draft) return;
    const saved = await store.save(draft);
    if (saved) {
      setDraft(saved);
      setSavedAt(Date.now());
    }
  };

  if (!draft) {
    return (
      <div className="admin" role="dialog" aria-modal="true" aria-label={t('curriculum.title')}>
        <header className="admin-hd">
          <div>
            <h1>{t('curriculum.title')}</h1>
            <p>{t('curriculum.subtitle')}</p>
          </div>
          <button className="a-close" onClick={onClose}>{t('btn.done')}</button>
        </header>
        <div className="admin-body">
          <Section title={t('curriculum.mine')}>
            {store.mine.length === 0 ? (
              <p className="a-empty">{t('curriculum.empty')}</p>
            ) : (
              <ul className="preset-list">
                {store.mine.map((c) => (
                  <li key={c.id}>
                    <span>
                      <b>{c.name}</b>
                      <small>{t('curriculum.levels', {
                        n: c.stages.reduce((n, s) => n + s.levels.length, 0),
                      })}</small>
                    </span>
                    <span className="preset-acts">
                      <button onClick={() => setDraft(c)}>{t('curriculum.edit')}</button>
                      <button onClick={() => {
                        if (window.confirm(t('curriculum.deleteAsk'))) void store.remove(c.id);
                      }}>{t('admin.delete')}</button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="a-row">
              <button
                className="a-reset"
                onClick={() => setDraft(duplicateCurriculum(store.builtin, t('curriculum.builtin')))}
              >
                {t('curriculum.duplicate')}
              </button>
              <button className="a-reset" onClick={() => setDraft(emptyCurriculum(t('curriculum.title')))}>
                {t('curriculum.new')}
              </button>
            </div>
            <p className="a-empty">{t('curriculum.readOnly')}</p>
          </Section>
        </div>
      </div>
    );
  }

  return (
    <div className="admin" role="dialog" aria-modal="true" aria-label={t('curriculum.title')}>
      <header className="admin-hd">
        <div>
          <h1>{t('curriculum.title')}</h1>
          <p>{t('curriculum.subtitle')}</p>
        </div>
        <button className="a-close" onClick={onClose}>{t('btn.done')}</button>
      </header>

      <div className="admin-body">
        <Section title={t('curriculum.name')}>
          <div className="a-row">
            <label className="a-field">
              <span className="a-label">{t('curriculum.name')}</span>
              <input
                className="a-select" type="text" value={draft.name}
                onChange={(e) => edit((c) => ({ ...c, name: e.target.value }))}
              />
            </label>
            <button className="a-reset" onClick={() => void save()} disabled={store.saving}>
              {store.saving ? t('curriculum.saving') : savedAt ? t('curriculum.saved') : t('curriculum.save')}
            </button>
            <button className="a-reset" onClick={() => setDraft(null)}>{t('btn.close')}</button>
          </div>
        </Section>

        {draft.stages.map((stage, si) => (
          <Section key={stage.id} title={displayName(stage.name, t)}>
            <div className="a-row">
              <label className="a-field">
                <span className="a-label">{t('curriculum.stageName')}</span>
                <input
                  className="a-select" type="text" value={displayName(stage.name, t)}
                  onChange={(e) => edit((c) => mapStage(c, stage.id, (s) => ({ ...s, name: e.target.value })))}
                />
              </label>
              <Select<Grade>
                label={t('curriculum.countsAs')}
                value={stage.grade}
                onChange={(v) => edit((c) => mapStage(c, stage.id, (s) => ({ ...s, grade: v })))}
                options={GRADE_VALUES.map((g) => ({ value: g, label: t('msg.grade', { g }) }))}
              />
            </div>
            <p className="a-hint">{t('curriculum.countsAsHelp')}</p>

            <ul className="cur-levels">
              {stage.levels.map((level, li) => {
                const key = `${stage.id}:${level.id}`;
                const open = openLevel === key;
                return (
                  <li key={level.id} className={open ? 'is-open' : ''}>
                    <div className="cur-level-hd">
                      <button
                        className="cur-level-name"
                        onClick={() => setOpenLevel(open ? null : key)}
                        aria-expanded={open}
                      >
                        <b>{displayName(level.name, t) || ruleLine(level.config, t)}</b>
                        <small>
                          {ruleLine(level.config, t)}
                          {' · '}
                          {/* `×` is bidi-neutral, so "5×4" inside an Arabic run
                              renders as "4×5". An LTR isolate keeps the board
                              dimensions in the order they were written. */}
                          <span dir="ltr">{num(level.config.rows)}×{num(level.config.cols)}</span>
                          {level.config.timerSec ? <> · ⏱ {num(level.config.timerSec)}</> : ''}
                          {level.config.puzzle ? ' · 🧩' : ''}
                        </small>
                      </button>
                      <span className="cur-acts">
                        <button onClick={() => edit((c) => mapStage(c, stage.id, (s) => ({ ...s, levels: move(s.levels, li, -1) })))} title={t('curriculum.up')}>↑</button>
                        <button onClick={() => edit((c) => mapStage(c, stage.id, (s) => ({ ...s, levels: move(s.levels, li, 1) })))} title={t('curriculum.down')}>↓</button>
                        <button onClick={() => edit((c) => mapStage(c, stage.id, (s) => ({ ...s, levels: s.levels.filter((x) => x.id !== level.id) })))} title={t('curriculum.remove')}>✕</button>
                      </span>
                    </div>
                    {open && (
                      <LevelForm
                        name={displayName(level.name, t)}
                        config={level.config}
                        onName={(v) => edit((c) => mapLevel(c, stage.id, level.id, (l) => ({ ...l, name: v })))}
                        onConfig={(patch) => edit((c) => mapLevel(c, stage.id, level.id, (l) => ({
                          ...l, config: sanitizeConfig({ ...l.config, ...patch }),
                        })))}
                      />
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="a-row">
              <button
                className="a-reset"
                onClick={() => edit((c) => mapStage(c, stage.id, (s) => ({
                  ...s, levels: [...s.levels, newLevel('', { ...base })],
                })))}
              >
                {t('curriculum.addLevel')}
              </button>
              <button className="a-reset" onClick={() => edit((c) => ({ ...c, stages: move(c.stages, si, -1) }))}>
                {t('curriculum.up')}
              </button>
              <button className="a-reset" onClick={() => edit((c) => ({ ...c, stages: move(c.stages, si, 1) }))}>
                {t('curriculum.down')}
              </button>
              <button
                className="a-reset"
                onClick={() => {
                  if (window.confirm(t('curriculum.removeStageAsk'))) {
                    edit((c) => ({ ...c, stages: c.stages.filter((x) => x.id !== stage.id) }));
                  }
                }}
              >
                {t('curriculum.remove')}
              </button>
            </div>
          </Section>
        ))}

        <Section title={t('curriculum.addStage')}>
          <button
            className="a-reset"
            onClick={() => edit((c) => ({
              ...c,
              stages: [...c.stages, newStage(Math.min(6, c.stages.length + 1) as Grade, t('curriculum.addStage'))],
            }))}
          >
            {t('curriculum.addStage')}
          </button>
        </Section>
      </div>
    </div>
  );
}

/** One exercise: the same fields the teacher panel exposes, scoped to a level. */
function LevelForm({ name, config, onName, onConfig }: {
  name: string;
  config: LevelConfig;
  onName: (v: string) => void;
  onConfig: (patch: Partial<LevelConfig>) => void;
}) {
  const { t, num } = useSettings();
  const hunt = useMemo(() => isHunt(config), [config]);

  return (
    <div className="cur-level-body">
      <div className="a-row">
        <label className="a-field">
          <span className="a-label">{t('curriculum.levelName')}</span>
          <input className="a-select" type="text" value={name} onChange={(e) => onName(e.target.value)} />
        </label>
        <Segmented<'pair' | 'hunt'>
          label={t('admin.mechanic')}
          value={hunt ? 'hunt' : 'pair'}
          onChange={(v) => onConfig({ rule: v === 'hunt' ? 'even' : 'sumTo' })}
          options={[
            { value: 'pair', label: t('admin.mechanicPair') },
            { value: 'hunt', label: t('admin.mechanicHunt') },
          ]}
        />
      </div>

      <div className="a-row">
        <Select<RuleId>
          label={t('admin.rule')}
          value={config.rule}
          onChange={(v) => onConfig({ rule: v, ruleTarget: RULES[v].defaultTarget })}
          options={(hunt ? HUNT_RULES : PAIR_RULES).map((r) => ({
            value: r, label: ruleLine({ ...config, rule: r }, t),
          }))}
        />
        {TARGETED_RULES.has(config.rule) && (
          <NumberField
            label={t('admin.target')} value={config.ruleTarget} min={2} max={144}
            onChange={(v) => onConfig({ ruleTarget: v })}
          />
        )}
      </div>

      <MultiplierField config={config} onChange={onConfig} />
      <RangeFields config={config} onChange={onConfig} />

      <div className="a-row">
        <NumberField label={t('admin.rows')} value={config.rows} min={2} max={12} onChange={(v) => onConfig({ rows: v })} />
        <NumberField label={t('admin.cols')} value={config.cols} min={2} max={12} onChange={(v) => onConfig({ cols: v })} />
        <NumberField
          label={t('admin.timer')} value={config.timerSec ?? 0} min={0} max={600} step={5}
          hint={config.timerSec === null ? t('admin.timerOff') : undefined}
          onChange={(v) => onConfig({ timerSec: v === 0 ? null : v })}
        />
      </div>

      <div className="a-row">
        <NumberField label={t('admin.adds')} value={config.adds} min={0} max={9} onChange={(v) => onConfig({ adds: v })} />
        <NumberField label={t('admin.hints')} value={config.hints} min={0} max={9} onChange={(v) => onConfig({ hints: v })} />
      </div>

      {hunt ? (
        <NumberField
          label={t('admin.density')} hint={t('admin.densityHelp')}
          value={Math.round(config.huntDensity * 100)} min={15} max={80} step={5}
          onChange={(v) => onConfig({ huntDensity: v / 100 })}
        />
      ) : (
        <Toggle
          label={t('admin.adjacency')} hint={t('admin.adjacencyHelp')}
          checked={config.adjacency} onChange={(v) => onConfig({ adjacency: v })}
        />
      )}

      <Toggle
        label={t('admin.puzzle')} hint={t('admin.puzzleHelp')}
        checked={config.puzzle}
        onChange={(v) => onConfig({ puzzle: v, ...(v ? { adds: 0 } : null) })}
      />

      <Segmented<LevelConfig['celebration']>
        label={t('admin.celebration')}
        value={config.celebration}
        onChange={(v) => onConfig({ celebration: v })}
        options={[
          { value: 'sparks', label: t('admin.celebrationSparks') },
          { value: 'word', label: t('admin.celebrationWord') },
          { value: 'full', label: t('admin.celebrationFull') },
        ]}
      />

      {config.rule === 'multipleOf' && (
        <Segmented<LevelConfig['multipleDisplay']>
          label={t('admin.multipleDisplay')}
          value={config.multipleDisplay}
          onChange={(v) => onConfig({ multipleDisplay: v })}
          options={[
            { value: 'factor', label: `${num(3)} × ${num(3)} = ${num(9)}` },
            { value: 'division', label: `${num(9)} ÷ ${num(3)} = ${num(3)}` },
          ]}
        />
      )}
    </div>
  );
}
